import classNames from "classnames";
import { DragEvent, ChangeEvent, useState, useEffect, useRef } from "react";
import useTranslations from "../../../core/i18n/useTranslations";
import PrimeModal from "../../../ui-lib/components/PrimeModal/PrimeModal";
import PrimeTooltip from "../../../ui-lib/components/PrimeTooltip/PrimeTooltip";

import styles from "./ObjectImportModal.module.css";
import { notify } from "../../../ui-lib/components/Alerts/Toast";
import { Row, Spacer } from "../../../components/Layout/Layout";
import Icon from "../../../ui-lib/components/Icon";
import useUser from "../../../core/user/useUser";
import { uploadObjectsExcel } from "../../../core/api/objects/objects";
import LoadingSpinner from "../../../ui-lib/components/Loading/LoadingSpinner";
import { getErrorKey } from "../../../components/Errors/ErrorAlert";

interface ObjectImportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ObjectImportModal = ({
  isOpen,
  onClose,
}: ObjectImportModalProps) => {
  const t = useTranslations();
  const { authenticatedRequest } = useUser();
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      setFile(null);
    };
  }, []);

  const validateAndSetFile = (fileToSave: File) => {
    const MAX_FILE_SIZE = 4 * 1024 * 1024; // 4MB in bytes

    if (fileToSave.size > MAX_FILE_SIZE) {
      notify({
        variant: "error",
        message: t("Errors:modal_import_file_size_error"),
      });
      setFile(null);
      return;
    }

    if (
      fileToSave &&
      (fileToSave.type === "application/vnd.ms-excel" ||
        fileToSave.type ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        fileToSave.type === "application/json")
    ) {
      setFile(fileToSave);
    } else {
      notify({
        variant: "error",
        message: t("Errors:modal_import_file_type_error"),
      });
      setFile(null);
    }
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    validateAndSetFile(selectedFile as File);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files?.[0];
    validateAndSetFile(droppedFile);
  };

  const handleCancelUpload = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setFile(null);
  };

  const handleUpload = async () => {
    if (!file) {
      return;
    }
    setIsLoading(true);
    abortControllerRef.current = new AbortController();

    const formData = new FormData();
    formData.append("file", file);

    try {
      await uploadObjectsExcel(
        formData,
        authenticatedRequest,
        abortControllerRef.current.signal,
      );
      setIsSuccessModalOpen(true);
    } catch (err) {
      const errorKey = getErrorKey(err);
      notify({
        message: t(`Errors:${errorKey}`),
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onCloseModal = () => {
    setFile(null);
    setIsSuccessModalOpen(false);
    onClose();
  };

  return (
    <>
      <PrimeModal
        isOpen={isOpen}
        onClose={onCloseModal}
        withHeader
        header={t("Objects:modal_import_header")}
        withFooter
        submitBtn={{
          disabled: !file || isLoading,
          text: t("Objects:import"),
          onClick: () => {
            handleUpload();
          },
        }}
        cancelBtn={{
          text: t("Common:cancel"),
          onClick: onCloseModal,
        }}
        fixedWidth="880px"
      >
        <p className="grey-600">{t("Objects:object_import_description")}</p>
        <Spacer size={16} />
        <Row type="left">
          <p>{t("Common:file_format")}:</p>
          <Icon className={styles.excelIcon} color="none" name="excel_logo" />
          <p className={styles.excelText}>{t("Common:excel")}</p>
          <Icon className={styles.excelIcon} color="none" name="json_logo" />
          <p className={styles.excelText}>{t("Common:json")}</p>
        </Row>
        <Spacer size={16} />
        <div className={styles.limitations}>
          <p className="grey-600">{t("Objects:limitations_header")}:</p>
          <ul>
            <li>{t("Objects:limitations_100_objects")}</li>
            <li>{t("Objects:limitations_100_transmitters")}</li>
            <li>{t("Objects:limitations_200_contacts")}</li>
          </ul>
        </div>
        <Spacer size={16} />
        <div>
          {!file ? (
            <div
              className={classNames(styles.dropArea, {
                [styles.dragging]: isDragging,
              })}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <label>
                <Row className={styles.dropAreaContent}>
                  <Icon color="Primary-700" name="upload" />
                  <Spacer size={8} />
                  <p className={styles.dropAreaText}>
                    {t("Objects:modal_import_file_upload")}
                  </p>
                </Row>
                <Spacer size={8} />
                <p className={styles.dropAreaDescription}>
                  {t("Objects:import_max_file_size")}
                </p>
                <input
                  type="file"
                  accept=".xls,.xlsx,.json"
                  onChange={handleFileChange}
                  className={styles.fileInput}
                />
              </label>
            </div>
          ) : (
            <Row className={styles.dropArea} type="space">
              <Row type="left">
                <Icon
                  color="none"
                  name={
                    file.type === "application/json"
                      ? "json_logo"
                      : "excel_logo"
                  }
                  size={36}
                />
                <Spacer size={8} />
                <p>{file.name}</p>
                {isLoading && (
                  <>
                    <Spacer size={8} />
                    <LoadingSpinner size={20} />
                  </>
                )}
              </Row>
              <PrimeTooltip target="#trashIcon" position="top" />
              <span id="trashIcon" data-pr-tooltip={t("Common:delete")}>
                <Icon
                  name="trash"
                  color="Grey-800"
                  size={24}
                  onClick={handleCancelUpload}
                />
              </span>
            </Row>
          )}
        </div>
      </PrimeModal>
      <PrimeModal
        isOpen={isSuccessModalOpen}
        onClose={onCloseModal}
        withHeader
        header={t("Objects:modal_import_success_header")}
        withFooter
        cancelBtn={{
          text: t("Common:ok"),
          onClick: onCloseModal,
        }}
        fixedWidth="576px"
      >
        <p className="grey-600">
          {t("Objects:modal_import_success_description")}
        </p>
      </PrimeModal>
    </>
  );
};
